/**
 * Opens the Klarna On-site Messaging interstitial when "learn more" is
 * activated. Klarna has no public open() API; the modal only launches from
 * a placement CTA. See https://docs.klarna.com/acquirer/klarna/on-site-messaging/
 *
 * @example
 * <payment-notice>
 *   <a data-payment-notice-learn-more href="/pages/zahlungsmethoden">Learn more</a>
 *   <klarna-placement data-key="credit-promotion-auto-size"></klarna-placement>
 * </payment-notice>
 */
const PAYMENT_NOTICE_PROVIDER_HOSTS = [
  'klarna-placement',
  '.klarna-onsite-messaging',
  'shopify-payment-terms',
];

if (!customElements.get('payment-notice')) {
  customElements.define(
    'payment-notice',
    class PaymentNotice extends HTMLElement {
      connectedCallback() {
        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);
        this.refreshKlarnaPlacements();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }

      /**
       * @param {MouseEvent} event
       * @returns {void}
       */
      onClick(event) {
        const trigger = event.target.closest('[data-payment-notice-learn-more]');

        if (!trigger || !this.contains(trigger)) {
          return;
        }

        if (this.openProviderLearnMore()) {
          event.preventDefault();
        }
      }

      /**
       * Asks Klarna OSM to re-read placement amounts after a variant change.
       *
       * @returns {void}
       */
      refreshKlarnaPlacements() {
        if (!this.querySelector('klarna-placement')) {
          return;
        }

        window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
        window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });
      }

      /**
       * Clicks a Klarna OSM or Shopify payment-terms CTA if one exists.
       *
       * @returns {boolean}
       */
      openProviderLearnMore() {
        const host = this.findProviderHost();

        if (!host) {
          return false;
        }

        if (this.clickLearnMoreInRoot(host)) {
          return true;
        }

        if (host.shadowRoot && this.clickLearnMoreInRoot(host.shadowRoot)) {
          return true;
        }

        return false;
      }

      /**
       * Prefer the placement inside this block when it has a CTA, then any page-level OSM host.
       *
       * @returns {Element|null}
       */
      findProviderHost() {
        const localPlacement = this.querySelector('klarna-placement');

        if (localPlacement && this.rootHasLearnMoreCta(localPlacement)) {
          return localPlacement;
        }

        for (const selector of PAYMENT_NOTICE_PROVIDER_HOSTS) {
          const host = document.querySelector(selector);

          if (host && host !== localPlacement && this.rootHasLearnMoreCta(host)) {
            return host;
          }
        }

        return null;
      }

      /**
       * @param {Element} host
       * @returns {boolean}
       */
      rootHasLearnMoreCta(host) {
        if (this.findLearnMoreCta(host)) {
          return true;
        }

        if (host.shadowRoot && this.findLearnMoreCta(host.shadowRoot)) {
          return true;
        }

        return false;
      }

      /**
       * @param {ParentNode} root
       * @returns {Element|null}
       */
      findLearnMoreCta(root) {
        const control = root.querySelector(
          '[part="osm-cta"], .osm-cta, button, a, [role="button"], [aria-haspopup]'
        );

        if (!control || control.hasAttribute('data-payment-notice-learn-more')) {
          return null;
        }

        return control;
      }

      /**
       * @param {ParentNode} root
       * @returns {boolean}
       */
      clickLearnMoreInRoot(root) {
        const control = this.findLearnMoreCta(root);

        if (!control) {
          return false;
        }

        control.click();

        return true;
      }
    }
  );
}
